import { OrderSide, Book, ApiResponse, Order } from '../types';
import { store } from '../state/store';
import { el } from './selectors';

export function initShortcuts({
  books,
  updateNotional,
  placeOrder,
  setCurrentSide,
  refreshOrderbook,
}: {
  books: Map<string, Book>;
  updateNotional: () => void;
  placeOrder: (order: Order) => Promise<ApiResponse<{ notional?: number }>>;
  setCurrentSide: (side: OrderSide) => void;
  refreshOrderbook: () => Promise<void>;
}): void {
  document.addEventListener('keydown', async (e) => {
    const target = e.target as HTMLElement;
    const tag = target?.tagName?.toLowerCase() ?? '';
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

    async function place(side: OrderSide, tokenId: string, price: number, shares: number): Promise<void> {
      if (!el.assetSelect || !el.priceInp || !el.sizeInp || !el.orderResult) return;
      el.assetSelect.value = tokenId;
      setCurrentSide(side);
      try {
        const book = books?.get(tokenId);
        const dec = book?.decimals ?? 2;
        el.priceInp.value = price.toFixed(dec);
      } catch {
        el.priceInp.value = price.toFixed(2);
      }
      el.sizeInp.value = String(shares);
      updateNotional();
      el.orderResult.textContent = '';
      try {
        // Route through renderer safe order path if available via global
        const safe = (window as any)?.__pmSafePlaceOrder as (o: Order) => Promise<ApiResponse<{ notional?: number }>>;
        const res = safe ? await safe({ tokenId, price, shares, side, orderType: 'GTC' }) : await placeOrder({ tokenId, price, shares, side, orderType: 'GTC' });
        if (res.ok) {
          const notional = (res.data as { notional?: number })?.notional;
          const usd = notional ?? price * shares;
          el.orderResult.textContent = `${side === 'BUY' ? 'Shortcut BUY' : 'Shortcut SELL'} ${shares} @ ${price.toFixed(2)} (USDC ${usd.toFixed(2)})`;
        } else {
          el.orderResult.textContent = `Shortcut ${side} error: ` + res.error;
        }
      } catch (err) {
        const error = err as Error;
        el.orderResult.textContent = `Shortcut ${side} error: ` + (error?.message ?? err);
      }
    }

    if ((e.key === 'r' || e.key === 'R') && typeof refreshOrderbook === 'function') {
      e.preventDefault();
      try { await refreshOrderbook(); } catch {}
      return;
    }

    const tokenIdA = el.teamAMap?.value
      || el.assetSelect?.options?.[0]?.value
      || '';
    const tokenIdB = el.teamBMap?.value
      || el.assetSelect?.options?.[1]?.value
      || el.assetSelect?.options?.[0]?.value
      || '';

    const quickSize = el.quickSize;
    const getShares = (): number => {
      const val = Number(quickSize?.value);
      const effective = val > 0 ? val : 10;
      return Math.max(5, effective);
    };

    if (e.key === '1' && tokenIdA) {
      const book = books.get(tokenIdA);
      if (!book || !book.askPrices.length) return;
      const price = Number(book.askPrices[0]); // market buy hits best ask
      const minNotionalShares = price > 0 ? Math.ceil(1 / price) : 5;
      const shares = Math.max(getShares(), minNotionalShares);
      await place('BUY', tokenIdA, price, shares);
    }
    if (e.key === '2' && tokenIdA) {
      const book = books.get(tokenIdA);
      if (!book || !book.bidPrices.length) return;
      const price = Number(book.bidPrices[0]); // market sell hits best bid
      const minNotionalShares = price > 0 ? Math.ceil(1 / price) : 5;
      const desired = Math.max(getShares(), minNotionalShares);
      // Clamp to owned shares
      const bal = store.positionsByAssetId.get(tokenIdA);
      const owned = bal ? Math.floor(Number(bal) / 1e6) : 0;
      const shares = Math.max(0, Math.min(desired, owned));
      if (shares <= 0) return;
      await place('SELL', tokenIdA, price, shares);
    }
    // Removed offset-based shortcuts (3/4)

    if (e.key === '7' && tokenIdB) {
      const book = books.get(tokenIdB);
      if (!book || !book.askPrices.length) return;
      const price = Number(book.askPrices[0]); // market buy hits best ask
      const minNotionalShares = price > 0 ? Math.ceil(1 / price) : 5;
      const shares = Math.max(getShares(), minNotionalShares);
      await place('BUY', tokenIdB, price, shares);
    }
    if (e.key === '8' && tokenIdB) {
      const book = books.get(tokenIdB);
      if (!book || !book.bidPrices.length) return;
      const price = Number(book.bidPrices[0]); // market sell hits best bid
      const minNotionalShares = price > 0 ? Math.ceil(1 / price) : 5;
      const desired = Math.max(getShares(), minNotionalShares);
      // Clamp to owned shares
      const bal = store.positionsByAssetId.get(tokenIdB);
      const owned = bal ? Math.floor(Number(bal) / 1e6) : 0;
      const shares = Math.max(0, Math.min(desired, owned));
      if (shares <= 0) return;
      await place('SELL', tokenIdB, price, shares);
    }
    // Removed offset-based shortcuts (9/0)
  });
}
